import React, { useEffect, useState, useMemo } from 'react';

const Hero: React.FC = () => {
  const [displayText, setDisplayText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  const roles = useMemo(() => [
    'Technical Support Advisor',
    'Technical Analyst',
    'Software Developer',
    'API Integration Specialist',
    'Problem Solver'
  ], []);

  useEffect(() => {
    const currentRole = roles[currentIndex];
    const timeout = setTimeout(() => {
      if (!isDeleting) {
        if (displayText.length < currentRole.length) {
          setDisplayText(currentRole.slice(0, displayText.length + 1));
        } else {
          setTimeout(() => setIsDeleting(true), 2000);
        }
      } else {
        if (displayText.length > 0) {
          setDisplayText(displayText.slice(0, -1));
        } else {
          setIsDeleting(false);
          setCurrentIndex((prev) => (prev + 1) % roles.length);
        }
      }
    }, isDeleting ? 50 : 100);

    return () => clearTimeout(timeout);
  }, [displayText, currentIndex, isDeleting, roles]);

  const handleScrollToContact = () => {
    const element = document.querySelector('#contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleScrollToProjects = () => {
    const element = document.querySelector('#projects');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="min-h-screen flex items-center justify-center relative overflow-hidden pt-20">
      {/* Animated Background */}
      <div className="absolute inset-0 particles-container hero-gradient"></div>
      
      {/* Floating Particles */}
      <div className="absolute inset-0">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-2 h-2 bg-primary/20 rounded-full animate-particleFloat"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 20}s`,
              animationDuration: `${15 + Math.random() * 10}s`
            }}
          ></div>
        ))}
      </div>

      {/* Hero Content */}
      <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto">
        <div className="hero-content">
          <h1 className="text-5xl sm:text-6xl md:text-7xl font-bold mb-6 leading-tight">
            Hi, I'm <span className="gradient-text">Kirat Mehta</span>
          </h1>
          
          <div className="text-xl sm:text-2xl md:text-3xl text-gray-300 mb-8 min-h-[3rem] flex items-center justify-center">
            <span className="mr-2">I'm a</span>
            <span className="text-primary font-semibold inline-block min-w-[280px] sm:min-w-[320px] text-left">
              {displayText}
              <span className="animate-pulse">|</span>
            </span>
          </div>

          <p className="text-base sm:text-lg md:text-xl text-gray-400 mb-12 max-w-3xl mx-auto leading-relaxed">
            <i className="fas fa-map-marker-alt text-primary mr-2"></i>
            <strong>Ontario, Canada</strong> | Technical Support Analyst with <strong>4+ years</strong> of experience 
            at leading companies including Concentrix, IT Infocom, and Tecsys Inc. Specializing in 
            <strong> C#, MS SQL, REST/SOAP APIs</strong>, and advanced troubleshooting. <strong>Bilingual</strong> in English & French.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-center mb-12">
            <button
              onClick={handleScrollToContact}
              className="w-full sm:w-auto cta-btn bg-primary hover:bg-primary/90 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 transform hover:scale-105 pulse-btn shadow-lg hover:shadow-primary/50"
            >
              <i className="fas fa-envelope mr-2"></i>
              Get In Touch
            </button>
            
            <button
              onClick={handleScrollToProjects}
              className="w-full sm:w-auto cta-btn border-2 border-primary text-primary hover:bg-primary hover:text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-primary/50"
            >
              <i className="fas fa-code mr-2"></i>
              View My Work
            </button>
          </div>

          {/* Social Links */}
          <div className="flex justify-center space-x-6">
            <a
              href="https://github.com/SilentMic"
              target="_blank"
              rel="noopener noreferrer"
              className="social-link text-gray-400 hover:text-primary text-2xl transition-all duration-300"
              aria-label="GitHub Profile"
            >
              <i className="fab fa-github"></i>
            </a>
            <a
              href="https://www.linkedin.com/in/kiratmehta/"
              target="_blank"
              rel="noopener noreferrer"
              className="social-link text-gray-400 hover:text-primary text-2xl transition-all duration-300"
              aria-label="LinkedIn Profile"
            >
              <i className="fab fa-linkedin"></i>
            </a>
            <a
              href="mailto:kiratmehta92@gmail.com"
              className="social-link text-gray-400 hover:text-primary text-2xl transition-all duration-300"
              aria-label="Email"
            >
              <i className="fas fa-envelope"></i>
            </a>
            <a
              href="tel:+12269347837"
              className="social-link text-gray-400 hover:text-primary text-2xl transition-all duration-300"
              aria-label="Phone"
            >
              <i className="fas fa-phone"></i>
            </a>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-gray-400 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-gray-400 rounded-full mt-2 animate-pulse"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;